import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { AuthContext } from '../../components/AuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  FaWhatsapp, FaPlug, FaQrcode, FaCog, FaUsers, FaUserTie,
  FaPaperPlane, FaCheckCircle, FaTimesCircle, FaSpinner,
  FaClipboardList, FaFileInvoiceDollar, FaBell, FaCalendarAlt,
  FaToggleOn, FaToggleOff, FaHistory, FaSyncAlt, FaInfoCircle,
  FaPlus, FaTrash, FaEye, FaFilePdf, FaDownload, FaPhoneAlt, FaTruck
} from 'react-icons/fa';

const WhatsAppAutomations = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode state from localStorage
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Connection state
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, phone: null, qr: null });
  const [connecting, setConnecting] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [pollingQr, setPollingQr] = useState(false);

  // Config state
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Employees
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  // Employee reports config
  const [employeeReports, setEmployeeReports] = useState([]);
  
  // Master automations
  const [masterAutomations, setMasterAutomations] = useState([
    { type: 'item-price-update', isActive: false, description: 'Notify customers when item price changes for offers in pending quotations' },
    { type: 'reference-commission', isActive: false, description: 'Send commission report or paid commission PDFs to references' },
    { type: 'quotation-send', isActive: false, description: 'Allow employees to send quotation PDFs to recipients via WhatsApp' },
    { type: 'tally-quotation-send', isActive: false, description: 'Allow employees to send tally quotation PDFs to recipients via WhatsApp' },
    { type: 'delivery-order-send', isActive: false, description: 'Allow employees to send delivery order PDFs to recipients via WhatsApp' }
  ]);

  // Message logs
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsPagination, setLogsPagination] = useState({ total: 1, count: 0 });
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState({ status: '', automationType: '' });

  // Active tab
  const [activeTab, setActiveTab] = useState('connection');

  // Report preview
  const [previewEmployee, setPreviewEmployee] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Stable blob URL for PDF preview — created once per previewData change, cleaned up properly
  const pdfBlobUrl = useMemo(() => {
    if (!previewData?.pdf) return null;
    const binaryStr = atob(previewData.pdf);
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  }, [previewData?.pdf]);

  useEffect(() => {
    return () => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); };
  }, [pdfBlobUrl]);

  // Queue info
  const [queueLength, setQueueLength] = useState(0);

  // Validate numbers
  const [validationResults, setValidationResults] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);

  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 30000,
    withCredentials: true,
  });

  const makeRequest = useCallback(async (method, url, data) => {
    const token = localStorage.getItem('token');
    if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    if (method === 'get') return api.get(url, { params: data });
    return api[method](url, data);
  }, [api]);

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
    fetchEmployees();
  }, []);

  // Poll QR while connecting
  useEffect(() => {
    let interval;
    if (pollingQr) {
      interval = setInterval(async () => {
        try {
          const res = await makeRequest('get', '/whatsapp-automations/qr');
          if (res.data.success) {
            if (res.data.data.connected) {
              setConnectionStatus({ connected: true, phone: res.data.data.phone, qr: null });
              setQrCode(null);
              setPollingQr(false);
              setConnecting(false);
              Swal.fire({ icon: 'success', title: 'Connected!', text: `WhatsApp connected: ${res.data.data.phone}`, timer: 2000 });
            } else if (res.data.data.qr) {
              setQrCode(res.data.data.qr);
            }
          }
        } catch (e) { /* ignore poll errors */ }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [pollingQr, makeRequest]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await makeRequest('get', '/whatsapp-automations/config');
      if (res.data.success && res.data.data) {
        setConfig(res.data.data);
        if (res.data.data.masterAutomations?.length) {
          // Merge server config with defaults so newer automation types are always present
          setMasterAutomations(prev => {
            const serverMap = new Map(res.data.data.masterAutomations.map(m => [m.type, m]));
            return prev.map(defaultItem => serverMap.get(defaultItem.type) || defaultItem);
          });
        }
        if (res.data.data.employeeReports?.length) setEmployeeReports(res.data.data.employeeReports);
        if (res.data.data.liveStatus) setConnectionStatus(res.data.data.liveStatus);
        if (res.data.data.queueLength !== undefined) setQueueLength(res.data.data.queueLength);
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const res = await makeRequest('get', '/whatsapp-automations/employees');
      if (res.data.success) setEmployees(res.data.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const res = await makeRequest('post', '/whatsapp-automations/connect');
      if (res.data.success) {
        if (res.data.data.connected) {
          setConnectionStatus({ connected: true, phone: res.data.data.phone, qr: null });
          setConnecting(false);
          Swal.fire({ icon: 'success', title: 'Already Connected!', timer: 1500 });
        } else {
          setPollingQr(true);
          if (res.data.data.qr) setQrCode(res.data.data.qr);
        }
      }
    } catch (error) {
      setConnecting(false);
      Swal.fire({ icon: 'error', title: 'Connection Failed', text: error.response?.data?.message || error.message });
    }
  };

  const handleDisconnect = async () => {
    const result = await Swal.fire({
      title: 'Disconnect WhatsApp?',
      text: 'This will log out the connected WhatsApp account.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Disconnect'
    });
    if (!result.isConfirmed) return;

    try {
      await makeRequest('post', '/whatsapp-automations/disconnect');
      setConnectionStatus({ connected: false, phone: null, qr: null });
      setQrCode(null);
      setPollingQr(false);
      Swal.fire({ icon: 'success', title: 'Disconnected', timer: 1500 });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to disconnect' });
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const res = await makeRequest('post', '/whatsapp-automations/config', {
        masterAutomations,
        employeeReports,
        messageGapSeconds: config?.messageGapSeconds || 60,
        typingDelayMs: config?.typingDelayMs || 3000,
        onlineDelaySeconds: config?.onlineDelaySeconds || 15
      });
      if (res.data.success) {
        setConfig(res.data.data);
        Swal.fire({ icon: 'success', title: 'Saved!', timer: 1500 });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Save Failed', text: error.response?.data?.message || error.message });
    } finally {
      setSaving(false);
    }
  };

  const addEmployeeReport = (employeeId) => {
    if (employeeReports.find(r => r.employee === employeeId || r.employee?._id === employeeId)) {
      Swal.fire({ icon: 'warning', title: 'Already Added', text: 'This employee is already configured.' });
      return;
    }
    setEmployeeReports(prev => [...prev, {
      employee: employeeId,
      isActive: true,
      includePendingDeliveryOrders: true,
      includePendingQuotationsNew: true,
      includePendingTallyQuotations: true,
      includePendingActivityLogs: true,
      includeOverdueReminders: true,
      includeTomorrowReminders: true,
      scheduleTime: '09:00',
      scheduleDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    }]);
  };

  const removeEmployeeReport = (index) => {
    setEmployeeReports(prev => prev.filter((_, i) => i !== index));
  };

  const updateEmployeeReport = (index, field, value) => {
    setEmployeeReports(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const toggleMasterAutomation = (index) => {
    setMasterAutomations(prev => prev.map((m, i) => i === index ? { ...m, isActive: !m.isActive } : m));
  };

  const sendEmployeeReport = async (employeeId) => {
    if (!connectionStatus.connected) {
      Swal.fire({ icon: 'warning', title: 'Not Connected', text: 'Connect WhatsApp first.' });
      return;
    }
    const result = await Swal.fire({
      title: 'Send Report PDF?',
      text: 'This will generate and send the daily report PDF (last 30 days) to this employee via WhatsApp.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send'
    });
    if (!result.isConfirmed) return;

    try {
      const res = await makeRequest('post', '/whatsapp-automations/send-employee-report', { employeeId });
      if (res.data.success) {
        Swal.fire({ icon: 'success', title: 'Queued!', text: `Position in queue: ${res.data.queuePosition}`, timer: 2000 });
        setQueueLength(res.data.queuePosition);
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Failed', text: error.response?.data?.message || error.message });
    }
  };

  const fetchPreview = async (employeeId) => {
    try {
      setPreviewLoading(true);
      setPreviewEmployee(employeeId);
      const res = await makeRequest('get', `/whatsapp-automations/employee-report-preview/${employeeId}`);
      if (res.data.success) setPreviewData(res.data.data);
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchLogs = async (page = 1) => {
    try {
      setLogsLoading(true);
      const params = { page, limit: 20, ...logsFilter };
      const res = await makeRequest('get', '/whatsapp-automations/logs', params);
      if (res.data.success) {
        setLogs(res.data.data);
        setLogsPagination(res.data.pagination);
        setLogsPage(page);
      }
    } catch (error) {
      console.error('Logs error:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'logs') fetchLogs(1);
  }, [activeTab, logsFilter]);

  const validateNumbers = async () => {
    try {
      setValidationLoading(true);
      setValidationResults(null);
      const res = await makeRequest('get', '/whatsapp-automations/validate-employee-numbers');
      if (res.data.success) {
        setValidationResults(res.data);
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data?.message || 'Failed to validate numbers' });
    } finally {
      setValidationLoading(false);
    }
  };

  const exportValidationCSV = () => {
    if (!validationResults?.data) return;
    const headers = ['Name', 'Role', 'Country Code', 'Phone', 'Status', 'Message'];
    const rows = validationResults.data.map(r => [
      r.name, r.role, r.countryCode, r.phone, r.status, r.message
    ]);
    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `WhatsApp_Number_Validation_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const exportEmployeeReportsCSV = () => {
    if (!employeeReports.length) return;
    const headers = ['Name', 'Role', 'Phone', 'Active', 'Schedule Time', 'Schedule Days', 'Pending DOs', 'Pending Quotations', 'Pending Tally Quotations', 'Pending Activity Logs', 'Overdue Reminders', 'Tomorrow Reminders'];
    const rows = employeeReports.map(report => {
      const empId = report.employee?._id || report.employee;
      const emp = employees.find(e => e._id === empId) || report.employee;
      return [
        emp?.name || empId,
        emp?.role?.name || 'Employee',
        emp?.phoneNumbers?.[0]?.number || 'No phone',
        report.isActive ? 'Yes' : 'No',
        report.scheduleTime || '09:00',
        (report.scheduleDays || []).join('; '),
        report.includePendingDeliveryOrders ? 'Yes' : 'No',
        report.includePendingQuotationsNew ? 'Yes' : 'No',
        report.includePendingTallyQuotations ? 'Yes' : 'No',
        report.includePendingActivityLogs ? 'Yes' : 'No',
        report.includeOverdueReminders ? 'Yes' : 'No',
        report.includeTomorrowReminders ? 'Yes' : 'No'
      ];
    });
    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Employee_Report_Automations_${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Styling helpers
  const bg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = darkMode ? 'bg-gray-800' : 'bg-white';
  const text = darkMode ? 'text-gray-100' : 'text-gray-900';
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-700 text-gray-100 border-gray-600' : 'bg-white text-gray-900 border-gray-300';
  const hoverBg = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50';

  if (loading) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <FaSpinner className="animate-spin text-green-500 text-4xl" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} ${text} transition-colors duration-300`}>
      {/* Header */}
      <div className={`${cardBg} shadow-sm border-b ${border} p-4 md:p-6`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <FaWhatsapp className="text-green-500 text-3xl" />
            <div>
              <h1 className={`text-xl md:text-2xl font-bold ${text}`}>WhatsApp Automations</h1>
              <p className={textMuted}>Configure and manage WhatsApp message automations</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Queue indicator */}
            {queueLength > 0 && (
              <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                Queue: {queueLength}
              </span>
            )}
            {/* Connection badge */}
            <span className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
              connectionStatus.connected 
                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            }`}>
              {connectionStatus.connected ? <FaCheckCircle /> : <FaTimesCircle />}
              {connectionStatus.connected ? `Connected: ${connectionStatus.phone}` : 'Disconnected'}
            </span>
            {/* Dark mode toggle removed - uses global localStorage darkMode */}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`${cardBg} border-b ${border} px-4 md:px-6`}>
        <div className="flex gap-1 overflow-x-auto">
          {[
            { key: 'connection', label: 'Connection', icon: FaPlug },
            { key: 'masters', label: 'Master Automations', icon: FaCog },
            { key: 'employees', label: 'Employee Reports', icon: FaUsers },
            { key: 'validate', label: 'Validate Numbers', icon: FaPhoneAlt },
            { key: 'logs', label: 'Message Logs', icon: FaHistory }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-green-500 text-green-600'
                  : `border-transparent ${textMuted} ${hoverBg}`
              }`}
            >
              <tab.icon /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* CONNECTION TAB */}
        {activeTab === 'connection' && (
          <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-6`}>
            <h2 className={`text-lg font-semibold mb-4 ${text}`}>WhatsApp Connection</h2>
            
            {connectionStatus.connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <FaCheckCircle className="text-green-500 text-2xl" />
                  <div>
                    <p className="font-semibold text-green-800 dark:text-green-200">Connected</p>
                    <p className="text-sm text-green-600 dark:text-green-400">Phone: {connectionStatus.phone}</p>
                    {config?.lastConnectedAt && (
                      <p className="text-xs text-green-500">Since: {new Date(config.lastConnectedAt).toLocaleString()}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <FaTimesCircle /> Disconnect
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {qrCode ? (
                  <div className="text-center space-y-4">
                    <p className={`${textMuted}`}>Scan this QR code with WhatsApp on your phone</p>
                    <div className="inline-block p-4 bg-white rounded-xl shadow-lg">
                      <img 
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrCode)}`} 
                        alt="WhatsApp QR Code" 
                        className="w-64 h-64"
                      />
                    </div>
                    <p className="text-sm text-yellow-600">
                      <FaInfoCircle className="inline mr-1" />
                      Waiting for scan... This will update automatically.
                    </p>
                  </div>
                ) : (
                  <div className="text-center space-y-4">
                    <div className="p-6 border-2 border-dashed rounded-xl border-gray-300 dark:border-gray-600">
                      <FaQrcode className={`mx-auto text-5xl mb-3 ${textMuted}`} />
                      <p className={textMuted}>Click Connect to generate a QR code</p>
                    </div>
                    <button
                      onClick={handleConnect}
                      disabled={connecting}
                      className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto disabled:opacity-50"
                    >
                      {connecting ? <FaSpinner className="animate-spin" /> : <FaWhatsapp />}
                      {connecting ? 'Connecting...' : 'Connect WhatsApp'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Connection info */}
            <div className={`mt-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-blue-50'} border ${darkMode ? 'border-gray-600' : 'border-blue-200'}`}>
              <h3 className={`font-medium mb-2 ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
                <FaInfoCircle className="inline mr-1" /> How it works
              </h3>
              <ul className={`text-sm space-y-1 ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                <li>• Messages are sent with at least 1 minute gap between each</li>
                <li>• WhatsApp status is set to online before sending</li>
                <li>• Messages are typed slowly to simulate natural behavior</li>
                <li>• Random delays of ~15 seconds between connection requests</li>
              </ul>
            </div>
          </div>
        )}

        {/* MASTER AUTOMATIONS TAB */}
        {activeTab === 'masters' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className={`text-lg font-semibold ${text}`}>Master Automations</h2>
              <button onClick={saveConfig} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                {saving ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Save
              </button>
            </div>

            {/* Item Price Update */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FaFileInvoiceDollar className="text-blue-500 text-xl mt-1" />
                  <div>
                    <h3 className={`font-semibold ${text}`}>Item Price Update Notifications</h3>
                    <p className={`text-sm mt-1 ${textMuted}`}>
                      When an item's price is updated for an offer type used in pending quotations,
                      the affected customers will be notified with the updated price information.
                    </p>
                    <p className={`text-xs mt-2 ${textMuted}`}>Type: Text message</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterAutomation(0)}
                  className="text-2xl"
                >
                  {masterAutomations[0]?.isActive 
                    ? <FaToggleOn className="text-green-500" /> 
                    : <FaToggleOff className={textMuted} />
                  }
                </button>
              </div>
            </div>

            {/* Reference Commission */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FaUserTie className="text-purple-500 text-xl mt-1" />
                  <div>
                    <h3 className={`font-semibold ${text}`}>Reference Commission Reports</h3>
                    <p className={`text-sm mt-1 ${textMuted}`}>
                      Send commission report PDFs or paid commission statements to references 
                      via WhatsApp after confirmation popup.
                    </p>
                    <p className={`text-xs mt-2 ${textMuted}`}>Type: PDF document</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterAutomation(1)}
                  className="text-2xl"
                >
                  {masterAutomations[1]?.isActive 
                    ? <FaToggleOn className="text-green-500" /> 
                    : <FaToggleOff className={textMuted} />
                  }
                </button>
              </div>
            </div>

            {/* Quotation Send */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FaFilePdf className="text-teal-500 text-xl mt-1" />
                  <div>
                    <h3 className={`font-semibold ${text}`}>Quotation Send via WhatsApp</h3>
                    <p className={`text-sm mt-1 ${textMuted}`}>
                      Allow employees to send quotation PDFs directly to customers/dealers 
                      via WhatsApp from the quotation page.
                    </p>
                    <p className={`text-xs mt-2 ${textMuted}`}>Type: PDF document</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterAutomation(2)}
                  className="text-2xl"
                >
                  {masterAutomations[2]?.isActive 
                    ? <FaToggleOn className="text-green-500" /> 
                    : <FaToggleOff className={textMuted} />
                  }
                </button>
              </div>
            </div>

            {/* Tally Quotation Send */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FaFilePdf className="text-cyan-500 text-xl mt-1" />
                  <div>
                    <h3 className={`font-semibold ${text}`}>Tally Quotation Send via WhatsApp</h3>
                    <p className={`text-sm mt-1 ${textMuted}`}>
                      Allow employees to send tally quotation PDFs directly to customers/dealers 
                      via WhatsApp from the tally quotation page.
                    </p>
                    <p className={`text-xs mt-2 ${textMuted}`}>Type: PDF document</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterAutomation(3)}
                  className="text-2xl"
                >
                  {masterAutomations[3]?.isActive 
                    ? <FaToggleOn className="text-green-500" /> 
                    : <FaToggleOff className={textMuted} />
                  }
                </button>
              </div>
            </div>

            {/* Delivery Order Send */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <FaTruck className="text-indigo-500 text-xl mt-1" />
                  <div>
                    <h3 className={`font-semibold ${text}`}>Delivery Order Send via WhatsApp</h3>
                    <p className={`text-sm mt-1 ${textMuted}`}>
                      Allow employees to send delivery order PDFs directly to customers/dealers 
                      via WhatsApp from the delivery order page.
                    </p>
                    <p className={`text-xs mt-2 ${textMuted}`}>Type: PDF document</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleMasterAutomation(4)}
                  className="text-2xl"
                >
                  {masterAutomations[4]?.isActive 
                    ? <FaToggleOn className="text-green-500" /> 
                    : <FaToggleOff className={textMuted} />
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* EMPLOYEE REPORTS TAB */}
        {activeTab === 'employees' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
              <h2 className={`text-lg font-semibold ${text}`}>Employee Report Automations</h2>
              <div className="flex gap-2">
                {employeeReports.length > 0 && (
                  <button onClick={exportEmployeeReportsCSV} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                    <FaDownload /> Export CSV
                  </button>
                )}
                <button onClick={saveConfig} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2">
                  {saving ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Save
                </button>
              </div>
            </div>

            {/* Add employee */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-4`}>
              <h3 className={`font-medium mb-3 ${text}`}><FaPlus className="inline mr-2 text-green-500" />Add Employee</h3>
              {employeesLoading ? (
                <FaSpinner className="animate-spin text-green-500" />
              ) : (
                <div className="flex flex-wrap gap-2">
                  <select
                    id="add-employee-select"
                    className={`flex-1 min-w-[200px] px-3 py-2 rounded-lg border ${inputBg} focus:ring-2 focus:ring-green-500`}
                    defaultValue=""
                    onChange={(e) => {
                      if (e.target.value) {
                        addEmployeeReport(e.target.value);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Select employee to add...</option>
                    {employees.filter(emp => 
                      !employeeReports.find(r => {
                        const rId = r.employee?._id || r.employee;
                        return rId === emp._id;
                      })
                    ).map(emp => (
                      <option key={emp._id} value={emp._id}>
                        {emp.name} {emp.phoneNumbers?.length ? `(${emp.phoneNumbers[0].number})` : '(no phone)'}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Employee report cards */}
            {employeeReports.length === 0 ? (
              <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-8 text-center`}>
                <FaUsers className={`mx-auto text-4xl mb-3 ${textMuted}`} />
                <p className={textMuted}>No employee reports configured. Add employees above.</p>
              </div>
            ) : (
              employeeReports.map((report, index) => {
                const empId = report.employee?._id || report.employee;
                const emp = employees.find(e => e._id === empId) || report.employee;
                const empName = emp?.name || empId;
                const empPhone = emp?.phoneNumbers?.[0]?.number || 'No phone';

                return (
                  <div key={index} className={`${cardBg} rounded-xl shadow-sm border ${border} p-5`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                          report.isActive ? 'bg-green-500' : 'bg-gray-400'
                        }`}>
                          {String(empName).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-semibold ${text}`}>{empName}</p>
                          <p className={`text-sm ${textMuted}`}>{empPhone} • {emp?.role?.name || 'Employee'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateEmployeeReport(index, 'isActive', !report.isActive)}
                          className="text-2xl"
                          title={report.isActive ? 'Disable' : 'Enable'}
                        >
                          {report.isActive ? <FaToggleOn className="text-green-500" /> : <FaToggleOff className={textMuted} />}
                        </button>
                        <button
                          onClick={() => fetchPreview(empId)}
                          className={`p-2 rounded-lg ${hoverBg}`}
                          title="Preview Report"
                        >
                          <FaEye className="text-blue-500" />
                        </button>
                        <button
                          onClick={() => sendEmployeeReport(empId)}
                          className={`p-2 rounded-lg ${hoverBg}`}
                          title="Send Now"
                          disabled={!connectionStatus.connected}
                        >
                          <FaPaperPlane className={connectionStatus.connected ? 'text-green-500' : textMuted} />
                        </button>
                        <button
                          onClick={() => removeEmployeeReport(index)}
                          className={`p-2 rounded-lg ${hoverBg}`}
                          title="Remove"
                        >
                          <FaTrash className="text-red-500" />
                        </button>
                      </div>
                    </div>

                    {/* Report sections toggles */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[
                        { key: 'includePendingDeliveryOrders', label: 'Pending Delivery Orders', icon: '📦' },
                        { key: 'includePendingQuotationsNew', label: 'Pending Quotations (New)', icon: '📝' },
                        { key: 'includePendingTallyQuotations', label: 'Pending Tally Quotations', icon: '📋' },
                        { key: 'includePendingActivityLogs', label: 'Pending Activity Logs (New)', icon: '📊' },
                        { key: 'includeOverdueReminders', label: 'Overdue Reminders', icon: '⚠️' },
                        { key: 'includeTomorrowReminders', label: "Tomorrow's Reminders", icon: '🔔' }
                      ].map(section => (
                        <label
                          key={section.key}
                          className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer ${hoverBg} ${
                            report[section.key] ? '' : 'opacity-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={report[section.key]}
                            onChange={() => updateEmployeeReport(index, section.key, !report[section.key])}
                            className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                          />
                          <span className="text-sm">{section.icon} {section.label}</span>
                        </label>
                      ))}
                    </div>

                    {/* Schedule */}
                    <div className={`mt-4 pt-4 border-t ${border}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                          <FaCalendarAlt className={textMuted} />
                          <span className={`text-sm ${textMuted}`}>Schedule:</span>
                          <input
                            type="time"
                            value={report.scheduleTime || '09:00'}
                            onChange={(e) => updateEmployeeReport(index, 'scheduleTime', e.target.value)}
                            className={`px-2 py-1 rounded border text-sm ${inputBg}`}
                          />
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, di) => {
                            const fullDay = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][di];
                            const isSelected = report.scheduleDays?.includes(fullDay);
                            return (
                              <button
                                key={day}
                                onClick={() => {
                                  const days = report.scheduleDays || [];
                                  const newDays = isSelected ? days.filter(d => d !== fullDay) : [...days, fullDay];
                                  updateEmployeeReport(index, 'scheduleDays', newDays);
                                }}
                                className={`px-2 py-1 text-xs rounded font-medium ${
                                  isSelected 
                                    ? 'bg-green-500 text-white' 
                                    : `${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Preview Modal */}
            {previewEmployee && (
              <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-5 mt-4`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`font-semibold ${text}`}>
                    <FaEye className="inline mr-2 text-blue-500" />
                    Report Preview (Last 30 Days)
                  </h3>
                  <div className="flex items-center gap-2">
                    {previewData?.pdf && (
                      <button
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `data:application/pdf;base64,${previewData.pdf}`;
                          link.download = previewData.fileName || 'Daily_Report.pdf';
                          link.click();
                        }}
                        className="px-3 py-1 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 flex items-center gap-1"
                      >
                        <FaFilePdf className="text-xs" /> Download PDF
                      </button>
                    )}
                    <button onClick={() => { setPreviewEmployee(null); setPreviewData(null); }} className={`${textMuted} hover:text-red-500`}>
                      <FaTimesCircle />
                    </button>
                  </div>
                </div>
                {previewLoading ? (
                  <div className="text-center py-8"><FaSpinner className="animate-spin text-green-500 text-2xl mx-auto" /></div>
                ) : previewData ? (
                  <div>
                    {/* Summary cards */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                      {previewData.sections?.pendingDeliveryOrders && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>📦 Pending DOs</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.pendingDeliveryOrders.count}</p>
                        </div>
                      )}
                      {previewData.sections?.pendingQuotations && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-green-300' : 'text-green-800'}`}>📝 Quotations</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.pendingQuotations.count}</p>
                        </div>
                      )}
                      {previewData.sections?.pendingTallyQuotations && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-purple-300' : 'text-purple-800'}`}>📋 Tally Quot.</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.pendingTallyQuotations.count}</p>
                        </div>
                      )}
                      {previewData.sections?.pendingActivityLogs && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>📊 Activity Logs</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.pendingActivityLogs.count}</p>
                        </div>
                      )}
                      {previewData.sections?.overdueReminders && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-red-300' : 'text-red-800'}`}>⚠️ Overdue</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.overdueReminders.count}</p>
                        </div>
                      )}
                      {previewData.sections?.tomorrowReminders && (
                        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-teal-50'}`}>
                          <p className={`text-xs font-medium ${darkMode ? 'text-teal-300' : 'text-teal-800'}`}>🔔 Tomorrow</p>
                          <p className="text-2xl font-bold mt-1">{previewData.sections.tomorrowReminders.count}</p>
                        </div>
                      )}
                    </div>
                    {/* PDF Preview iframe */}
                    {previewData.pdf && pdfBlobUrl && (
                      <iframe
                        src={pdfBlobUrl}
                        className="w-full rounded-lg border"
                        style={{ height: '500px' }}
                        title="Report PDF Preview"
                      />
                    )}
                  </div>
                ) : (
                  <p className={textMuted}>No preview data available.</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* MESSAGE LOGS TAB */}
        {/* VALIDATE NUMBERS TAB */}
        {activeTab === 'validate' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
              <h2 className={`text-lg font-semibold ${text}`}>Validate Employee WhatsApp Numbers</h2>
              <div className="flex gap-2">
                {validationResults?.data && (
                  <button onClick={exportValidationCSV} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm">
                    <FaDownload /> Export CSV
                  </button>
                )}
                <button onClick={validateNumbers} disabled={validationLoading || !connectionStatus.connected} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm">
                  {validationLoading ? <FaSpinner className="animate-spin" /> : <FaPhoneAlt />}
                  {validationLoading ? 'Validating...' : 'Run Validation'}
                </button>
              </div>
            </div>

            {!connectionStatus.connected && (
              <div className={`${cardBg} rounded-xl shadow-sm border border-yellow-500 p-4 flex items-center gap-3`}>
                <FaInfoCircle className="text-yellow-500 text-xl flex-shrink-0" />
                <p className={`text-sm ${text}`}>WhatsApp must be connected to validate numbers. Go to the Connection tab first.</p>
              </div>
            )}

            {validationResults && (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Total', value: validationResults.summary.total, color: 'bg-gray-500' },
                    { label: 'Valid', value: validationResults.summary.valid, color: 'bg-green-500' },
                    { label: 'Invalid', value: validationResults.summary.invalid, color: 'bg-red-500' },
                    { label: 'No Number', value: validationResults.summary.noNumber, color: 'bg-yellow-500' },
                    { label: 'Errors', value: validationResults.summary.errors, color: 'bg-orange-500' },
                  ].map(card => (
                    <div key={card.label} className={`${cardBg} rounded-xl shadow-sm border ${border} p-4 text-center`}>
                      <p className={`text-sm ${textMuted}`}>{card.label}</p>
                      <p className={`text-2xl font-bold mt-1 ${text}`}>{card.value}</p>
                      <div className={`h-1 w-10 mx-auto mt-2 rounded ${card.color}`} />
                    </div>
                  ))}
                </div>

                {/* Results table */}
                <div className={`${cardBg} rounded-xl shadow-sm border ${border} overflow-hidden`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                        <tr>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>#</th>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Name</th>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Role</th>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Phone</th>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Status</th>
                          <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Message</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${border}`}>
                        {validationResults.data.map((r, i) => (
                          <tr key={i} className={hoverBg}>
                            <td className={`px-4 py-3 ${textMuted}`}>{i + 1}</td>
                            <td className={`px-4 py-3 font-medium ${text}`}>{r.name}</td>
                            <td className={`px-4 py-3 ${textMuted}`}>{r.role}</td>
                            <td className={`px-4 py-3 ${text}`}>{r.countryCode !== '-' ? `${r.countryCode} ` : ''}{r.phone}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                r.status === 'valid' ? 'bg-green-100 text-green-800' :
                                r.status === 'invalid' ? 'bg-red-100 text-red-800' :
                                r.status === 'no_number' || r.status === 'empty' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {r.status === 'valid' && <FaCheckCircle className="inline mr-1" />}
                                {r.status === 'invalid' && <FaTimesCircle className="inline mr-1" />}
                                {r.status}
                              </span>
                            </td>
                            <td className={`px-4 py-3 text-xs ${textMuted}`}>{r.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {!validationResults && !validationLoading && (
              <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-8 text-center`}>
                <FaPhoneAlt className={`mx-auto text-4xl mb-3 ${textMuted}`} />
                <p className={textMuted}>Click "Run Validation" to check all employee phone numbers against WhatsApp.</p>
                <p className={`text-xs mt-2 ${textMuted}`}>This will verify each number without sending any messages.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'logs' && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
              <h2 className={`text-lg font-semibold ${text}`}>Message Logs</h2>
              <button onClick={() => fetchLogs(logsPage)} className={`p-2 rounded-lg ${hoverBg} ${text}`} title="Refresh">
                <FaSyncAlt />
              </button>
            </div>

            {/* Filters */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} p-4 flex flex-wrap gap-3`}>
              <select
                value={logsFilter.status}
                onChange={(e) => setLogsFilter(prev => ({ ...prev, status: e.target.value }))}
                className={`px-3 py-2 rounded-lg border ${inputBg}`}
              >
                <option value="">All Statuses</option>
                <option value="queued">Queued</option>
                <option value="sending">Sending</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={logsFilter.automationType}
                onChange={(e) => setLogsFilter(prev => ({ ...prev, automationType: e.target.value }))}
                className={`px-3 py-2 rounded-lg border ${inputBg}`}
              >
                <option value="">All Types</option>
                <option value="item-price-update">Price Update</option>
                <option value="reference-commission">Commission Report</option>
                <option value="employee-report">Employee Report</option>
                <option value="quotation-send">Quotation Send</option>
                <option value="tally-quotation-send">Tally Quotation Send</option>
                <option value="delivery-order-send">Delivery Order Send</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            {/* Logs table */}
            <div className={`${cardBg} rounded-xl shadow-sm border ${border} overflow-hidden`}>
              {logsLoading ? (
                <div className="p-8 text-center"><FaSpinner className="animate-spin text-green-500 text-2xl mx-auto" /></div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center">
                  <FaHistory className={`mx-auto text-4xl mb-3 ${textMuted}`} />
                  <p className={textMuted}>No message logs found.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Recipient</th>
                        <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Type</th>
                        <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Status</th>
                        <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Time</th>
                        <th className={`px-4 py-3 text-left font-medium ${textMuted}`}>Message</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${border}`}>
                      {logs.map(log => (
                        <tr key={log._id} className={hoverBg}>
                          <td className="px-4 py-3">
                            <p className={`font-medium ${text}`}>{log.recipientName || log.recipientPhone}</p>
                            <p className={`text-xs ${textMuted}`}>{log.recipientPhone}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.automationType === 'employee-report' ? 'bg-blue-100 text-blue-800' :
                              log.automationType === 'reference-commission' ? 'bg-purple-100 text-purple-800' :
                              log.automationType === 'item-price-update' ? 'bg-orange-100 text-orange-800' :
                              log.automationType === 'quotation-send' ? 'bg-teal-100 text-teal-800' :
                              log.automationType === 'tally-quotation-send' ? 'bg-cyan-100 text-cyan-800' :
                              log.automationType === 'delivery-order-send' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.automationType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`flex items-center gap-1 text-xs font-medium ${
                              log.status === 'sent' ? 'text-green-600' :
                              log.status === 'failed' ? 'text-red-600' :
                              log.status === 'sending' ? 'text-yellow-600' :
                              textMuted
                            }`}>
                              {log.status === 'sent' && <FaCheckCircle />}
                              {log.status === 'failed' && <FaTimesCircle />}
                              {log.status === 'sending' && <FaSpinner className="animate-spin" />}
                              {log.status}
                            </span>
                            {log.errorMessage && <p className="text-xs text-red-500 mt-1">{log.errorMessage}</p>}
                          </td>
                          <td className={`px-4 py-3 text-xs ${textMuted}`}>
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className={`px-4 py-3 text-xs ${textMuted} max-w-xs truncate`}>
                            {log.messageType === 'pdf' ? `📄 ${log.pdfFileName}` : (log.messageContent || '').substring(0, 80)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {logsPagination.total > 1 && (
                <div className={`flex items-center justify-between px-4 py-3 border-t ${border}`}>
                  <p className={`text-sm ${textMuted}`}>
                    Page {logsPage} of {logsPagination.total} ({logsPagination.count} total)
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => fetchLogs(logsPage - 1)}
                      disabled={logsPage <= 1}
                      className={`px-3 py-1 rounded border ${border} text-sm ${hoverBg} disabled:opacity-50`}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchLogs(logsPage + 1)}
                      disabled={logsPage >= logsPagination.total}
                      className={`px-3 py-1 rounded border ${border} text-sm ${hoverBg} disabled:opacity-50`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppAutomations;
